import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { LoadingService, MessagesService } from 'shared/domain';
import { SearchOptions, appendHttpParamsFromObject, objectToUrlParams, TreetopPropertiesProvider } from 'shared/util-common';

import { AttachmentReferencesResultDto, Attachment, AttachmentFilter } from '@treetop/shared/model';

@Injectable({
  providedIn: 'root',
})
export class AssetmanagementService {
  private readonly http = inject(HttpClient);
  private readonly loading = inject(LoadingService);
  private readonly messages = inject(MessagesService);

  baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  loadAttachments(
    query: AttachmentFilter,
    options: SearchOptions = { page: 1, itemsPerPage: 10 },
    includeParentOrganizations = true,
    forAllOrganizations = false,
  ): Observable<any> {
    const queryParams = objectToUrlParams(query);
    const optionParams = objectToUrlParams(options);
    const includeParentParam = appendHttpParamsFromObject({
      includeParentOrganizations: includeParentOrganizations,
      forAllOrganizations: forAllOrganizations,
    });
    const loadAttachments$ = this.http
      .get<any>(`${this.baseUrl}/attachments/?${queryParams}&${optionParams}&${includeParentParam}`)
      .pipe(
        catchError((err) => {
          this.messages.showError('CouldNotLoadAttachments');
          return throwError(err);
        }),
        shareReplay(),
      )
      .pipe(map((response) => response.list));
    this.loading.showLoaderUntilCompleted(loadAttachments$).subscribe();
    return loadAttachments$;
  }

  loadAttachmentReferences(attachmentId: string, includeAllVersions = false): Observable<AttachmentReferencesResultDto> {
    const $loadAttachmentReferences = this.http
      .get<AttachmentReferencesResultDto>(
        `${this.baseUrl}/attachments/${attachmentId}/references?includeAllVersions=${includeAllVersions.toString()}`,
      )
      .pipe(
        catchError((err) => {
          this.messages.showError('CouldNotLoadAttachmentReferences');
          return throwError(err);
        }),
        shareReplay(),
      );
    this.loading.showLoaderUntilCompleted($loadAttachmentReferences).subscribe();
    return $loadAttachmentReferences;
  }

  downloadAttachment(attachmentId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/attachments/${attachmentId}/download`, { observe: 'response', responseType: 'blob' }).pipe(
      catchError((err) => {
        this.messages.showError('ErrorDownloadingAttachment');
        return throwError(err);
      }),
    );
  }

  addAttachment(formData: FormData): Observable<any> {
    const $addAttachment = this.http.post<any>(`${this.baseUrl}/attachments`, formData).pipe(
      catchError((err) => {
        this.showAttachmentError(err);
        return throwError(err);
      }),
      tap(() => this.messages.showInfo('AttachmentSaved')),
      shareReplay(),
    );
    this.loading.showLoaderUntilCompleted($addAttachment).subscribe();
    return $addAttachment;
  }

  editAttachment(attachmentId: string, formData: FormData): Observable<Attachment> {
    return this.http.put<Attachment>(`${this.baseUrl}/attachments/${attachmentId}`, formData).pipe(
      catchError((err) => {
        this.showAttachmentError(err);
        return throwError(err);
      }),
      tap(() => this.messages.showInfo('AttachmentEdited')),
      shareReplay(),
    );
  }

  showAttachmentError(err) {
    if (err.error.Message) {
      this.messages.showError(err.error.Message);
    } else {
      // if status is 400 and the error array has the first element that starts with
      // Failed to read the request form. Request body too large.
      if (
        err.error.errors &&
        err.error.errors[''] &&
        err.error.errors[''].length == 1 &&
        err.error.errors[''][0].toUpperCase().indexOf('TOO LARGE') > 0
      ) {
        this.messages.showError('FileExceedsTheMaximumAllowedFileSize');
      } else {
        // Generic error
        this.messages.showError('UnknownErrorPleaseTryAgainLater');
      }
    }
  }

  deleteAttachment(attachmentId: string): Observable<boolean> {
    const $deleteAttachment = this.http.delete<boolean>(`${this.baseUrl}/attachments/${attachmentId}`).pipe(
      catchError((err) => {
        this.messages.showError('AttachmentDeleteError');
        return throwError(err);
      }),
      tap(() => this.messages.showInfo('AttachmentDeleted')),
      shareReplay(),
    );
    this.loading.showLoaderUntilCompleted($deleteAttachment).subscribe();
    return $deleteAttachment;
  }
}
